import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import type {UserData} from '@Libraries/user.library';

export const MY_STUFF_HASH_INDEX = 1;
export const MY_STUFF_HASH_PARAM_INDEX = 2;
export const MY_STUFF_NON_REACT_CONTENT_LOADED_EVENT = 'nonReactMSContentLoaded';

export type UserDataFromTeamsPayload = UserData & {
  email: string;
  profilePictureURLSmall: string;
};

export type TeamMembers = Record<string, UserDataFromTeamsPayload>;

export interface Team {
  idTeam: string;
  name: string;
  seats: number;
  type: TeamType;
  members: TeamMembers;
}

export enum TeamType {
  REGULAR,
  RESELLER,
  RESELLER_CLIENT,
}

export const isPageHashIncludedInList = (pageHash: string, pageHashes: Array<string>): boolean => {
  return pageHashes.length > 0 && pageHashes.includes(pageHash);
};

export const isPageRelatedToMyDesigns = (pageHash: string): boolean => {
  const myDesignsPagesList = [MY_STUFF_HASHES.myDesigns, MY_STUFF_HASHES.sharedWithMe, MY_STUFF_HASHES.search, MY_STUFF_HASHES.favorites, MY_STUFF_HASHES.recommendedForYou];

  if (!isDesignerActionCenterEnabled()) {
    myDesignsPagesList.push(MY_STUFF_HASHES.rejectedTemplates);
  }

  return isPageHashIncludedInList(pageHash, myDesignsPagesList);
};

export const isPageRelatedToMyEmails = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [
    MY_STUFF_HASHES.emailCampaigns,
    MY_STUFF_HASHES.emailCampaignSettings,
    MY_STUFF_HASHES.emailCampaignsShared,
    MY_STUFF_HASHES.emailCampaignPerformance,
    MY_STUFF_HASHES.emailCampaignAudience,
  ]);
};

export const isPageRelatedToEmailCampaigns = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaigns]);
};

export const isPageRelatedToEmailCampaignSettings = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaignSettings]);
};

export const isPageRelatedToEmailCampaignShared = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaignsShared]);
};


export const isPageRelatedToEmailCampaignPerformance = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.emailCampaignPerformance]);
};

export const isPageRelatedToSocialMediaAccounts = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.socialAccounts]);
};


export const isPageRelatedToSocialMediaPublishing = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.socialPosts, MY_STUFF_HASHES.socialAccounts, MY_STUFF_HASHES.sharedSocialPosts]);
};

export const isContentPlannerPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.contentPlanner;
};

export const isClassProjectsPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.classProjects;
};

export const isTrashPage = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.trash, MY_STUFF_HASHES.trashSocialPosts, MY_STUFF_HASHES.trashEmailCampaigns]);
};

export const isBrandKitsPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.brands;
};

export const isSocialMediaPostsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.socialPosts;
};

export const isSharedSocialMediaPostsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.sharedSocialPosts;
};

export const isSocialMediaAccountsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.socialAccounts;
};

export const isRecommendationsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.recommendedForYou;
};

export const shouldRecommendationsPageBeShown = (): boolean => {
  return !!window.reactPageProps.doesUserHaveRecommendationsAvailable;
};

export const isDesignerActionCenterEnabled = (): boolean => {
  return !!window.reactPageProps.isDesignerActionCenterEnabled;
};

export const isPagePartOfActionCenter = (pageHash: string = getMyStuffPageHash()): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.improveTemplates, MY_STUFF_HASHES.rejectedTemplates]);
};

export const isImproveTemplatesPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.improveTemplates;
};

export const isEmailMarketingCampaignsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.emailCampaigns;
};

export const isBusinessProfilesPage = (pageHash: string): boolean => {
  return pageHash === MY_STUFF_HASHES.businessProfile;
};

export const isPageRelatedToBusinessProfiles = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.businessProfile, MY_STUFF_HASHES.brands]);
};

export const isPageRelatedToEventsFeature = (pageHash: string): boolean => {
  return isPageRelatedToEvents(pageHash) || isPageRelatedToEventPromotion(pageHash);
};

export const isPageRelatedToEvents = (pageHash: string): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.events, MY_STUFF_HASHES.eventsCalendar, MY_STUFF_HASHES.eventsSharedWithMe]);
};

export const isPageRelatedToEventPromotion = (pageHash: string = getMyStuffPageHash()): boolean => {
  return isPageHashIncludedInList(pageHash, [MY_STUFF_HASHES.promoteEvent, MY_STUFF_HASHES.manageEvent]);
};

export const isEventsPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.events;
};

export const isEventsSharedPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.eventsSharedWithMe;
};

export const isEventsHomePage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.eventsCalendar;
};

export const isEventPromotionPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.promoteEvent;
};

export const isManageEventPage = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.manageEvent;
};

export const isPageRelatedToContacts = (pageHash: string = getMyStuffPageHash()): boolean => {
  return pageHash === MY_STUFF_HASHES.signupForms || pageHash === MY_STUFF_HASHES.mailingLists;
};

export const shouldSharedEventsNotificationBeShown = (): boolean => {
  return !!window.reactPageProps.shouldShowSharedEventsNotification;
};

/**
 * Extracts and returns the page hash from the passed window location hash, such
 * that it can be compared with one of the constants from MY_STUFF_HASHES.
 * @return {string}
 */
export const getMyStuffPageHash = (): string => {
  return `#/${window.location.hash.split('/')[MY_STUFF_HASH_INDEX]}`;
};

export const getParamsAfterMyStuffHash = (): string => {
  return window.location.hash.split('/')[MY_STUFF_HASH_PARAM_INDEX] ?? '';
};

export const goToMyStuff = (): void => {
  window.location.href = window.PMW.util.site_url('posters/mine');
};

export const isTeamAdmin = (): boolean => {
  return !!window.reactPageProps?.isTeamAdmin;
};

export const isExPremiumUser = (): boolean => {
  return !!window.reactPageProps?.isExPremiumUser;
};

export const isMoreOptionsItemSelectedInCompactMyStuffNav = (selectedItemIndexFromPrimaryList: number, navItemsLimit: number): boolean => {
  return selectedItemIndexFromPrimaryList === -1 || selectedItemIndexFromPrimaryList >= navItemsLimit;
};
// codeReviewShahrukhLater: move to teams-library? with better typing and a more generic payload ig
const isTeamFromReactPagePropsValid = (): boolean => {
  const teamPageProps = window.reactPageProps?.team ?? null;

  if (!teamPageProps) {
    return false;
  }

  return (
    typeof teamPageProps.idTeam === 'string' &&
    typeof teamPageProps.name === 'string' &&
    typeof teamPageProps.seats === 'number' &&
    typeof teamPageProps.type === 'number' &&
    typeof teamPageProps.members === 'object'
  );
};
export const getTeamFromReactPageProps = (): Team | null => {
  if (!isTeamFromReactPagePropsValid()) {
    return null;
  }

  return (window.reactPageProps?.team as Team) ?? null;
};

export const getTeamNameFromPageProps = (): string => {
  return getTeamFromReactPageProps()?.name ?? window.i18next.t('pmwjs_team');
};

export const getUserTeamIDFromPageProps = (): string => {
  return getTeamFromReactPageProps()?.idTeam ?? '';
};

export const isBottomBarHidden = (): boolean => {
  const bottomBar = document.querySelector('.js-mystuff-bottom-bar');

  if (!bottomBar) {
    return true;
  }

  return window.getComputedStyle(bottomBar).display === 'none';
};

export const hideMyStuffBottomBar = (): void => {
  document.querySelector('.js-mystuff-bottom-bar')?.classList?.add('_hidden');
};

export const showMyStuffBottomBar = (): void => {
  document.querySelector('.js-mystuff-bottom-bar')?.classList?.remove('_hidden');
};
